import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Box
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { passkitAPI } from '../../services/api';
import { logger } from '../../utils/logger';

interface EventDeleteDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  event: {
    id: string;
    name?: string;
    event_date: string;
    venue_name?: string;
    venue_id?: string;
  } | null;
  productionId: string;
}

const EventDeleteDialog: React.FC<EventDeleteDialogProps> = ({
  open,
  onClose,
  onSuccess,
  event,
  productionId
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!event) return;

    setLoading(true);
    setError(null);

    try {
      logger.log('🗑️ Deleting event:', event.id);

      if (!event.venue_id) {
        throw new Error('Venue ID is required for deleting event');
      }

      await passkitAPI.deleteEvent(event.id, {
        productionId: productionId,
        venueId: event.venue_id
      });

      onSuccess();
      handleClose();
    } catch (err: any) {
      logger.error('Error deleting event:', err);
      setError(err.response?.data?.detail || 'Greška pri brisanju događaja');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  if (!event) return null;

  const eventTime = new Date(event.event_date).toLocaleString('sr-RS', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Obriši događaj
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Typography variant="body1">
            Da li ste sigurni da želite da obrišete ovaj događaj?
          </Typography>

          <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Datum i vreme:
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {eventTime}
            </Typography>

            {event.venue_name && (
              <>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
                  Lokacija:
                </Typography>
                <Typography variant="body1">
                  {event.venue_name}
                </Typography>
              </>
            )}
          </Box>

          <Alert severity="warning">
            Ova akcija se ne može poništiti. Svi izdati tiketi za ovaj događaj
            će možda biti nevažeći.
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Otkaži
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          color="error"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Brisanje...' : 'Obriši događaj'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventDeleteDialog;
